import type { SolMetadata } from '@seek/sol-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Text,
  Stack,
  ButtonLink,
  IconArrow,
  Inline,
} from 'braid-design-system';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import striptags from 'striptags';

import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';

import { useSignedInDashboardContext } from '../SignedInDashboardContextProvider';
import { isCareerFeedCompanyEvp } from '../hooks/feed-types';

import translations from './.vocab';

import * as styles from './CompanyEVP.css';

const CompanyLogo = ({ src, inline }: { src: string; inline?: boolean }) => {
  const props = inline
    ? {
        borderRadius: 'standard' as const,
        boxShadow: 'borderNeutralLight' as const,
        position: 'absolute' as const,
      }
    : {};

  return (
    <Box
      component="img"
      src={src}
      padding="xxsmall"
      className={[
        styles.companyLogo,
        {
          [styles.inlineLogo]: inline,
        },
      ]}
      alt=""
      {...props}
    />
  );
};

const CompanyEVPBranding = ({
  cover,
  logo,
}: Pick<CompanyEVPProps, 'logo' | 'cover'>) =>
  cover && (
    <Box
      display="flex"
      position="relative"
      data={{ automation: 'company-evp-cover-container' }}
    >
      <Box
        borderRadius="standard"
        component="img"
        src={cover}
        width="full"
        alt=""
      />
      <CompanyLogo src={logo} inline={true} />
    </Box>
  );

type CompanySnippetViewedEventPayload = {
  rank: number;
  companyId: string;
  serviceToken: string;
};

export type CompanyEVPProps = {
  id: string;
  logo: string;
  name: string;
  summary: string;
  url: string;
  cover?: string;
  solMetadata?: SolMetadata;
  serviceToken?: string;
};

export const CompanyEVP = ({
  cover,
  logo,
  name,
  summary,
  url,
}: CompanyEVPProps) => {
  const { t } = useTranslations(translations);
  const hasCover = Boolean(cover);
  const summaryMaxLines = hasCover ? 4 : 3;
  const analyticsFacade = useAnalyticsFacade();

  const {
    careerFeedResult: { feedItems },
  } = useSignedInDashboardContext();

  const evpTrackingPayload = useMemo<
    CompanySnippetViewedEventPayload | undefined
  >(() => {
    const companyEvp = feedItems?.find((item) => isCareerFeedCompanyEvp(item));
    if (!companyEvp?.id || !companyEvp?.serviceToken) {
      return undefined;
    }

    const rank =
      (feedItems?.findIndex((item) => isCareerFeedCompanyEvp(item)) ?? -1) + 1;

    return {
      rank,
      companyId: companyEvp.id,
      serviceToken: companyEvp.serviceToken,
    };
  }, [feedItems]);

  const { ref: trackedComponent } = useInView({
    onChange: (inView) => {
      if (inView && evpTrackingPayload) {
        analyticsFacade.companySnippetViewed(evpTrackingPayload);
      }
    },
    delay: 500,
    threshold: 0.5,
    triggerOnce: true,
  });

  return (
    <Box
      display="flex"
      flexDirection={{ mobile: 'columnReverse', tablet: 'row' }}
      gap={{ mobile: 'xlarge', tablet: 'medium' }}
      padding="medium"
      ref={trackedComponent}
    >
      <Box width="full">
        <Stack space="small">
          {hasCover ? (
            <Text size="large" weight="medium">
              {name}
            </Text>
          ) : (
            <Inline space="small" alignY="center" collapseBelow="tablet">
              <CompanyLogo src={logo} />
              <Text size="large" weight="medium">
                {name}
              </Text>
            </Inline>
          )}

          <Text size="small" maxLines={summaryMaxLines}>
            {striptags(summary)}
          </Text>

          <Inline space="none">
            <ButtonLink
              href={url}
              target="_blank"
              variant="soft"
              iconPosition="trailing"
              icon={<IconArrow direction="right" />}
            >
              {t('View company')} &nbsp;
            </ButtonLink>
          </Inline>
        </Stack>
      </Box>

      {hasCover && (
        <Box width="full">
          <CompanyEVPBranding cover={cover} logo={logo} />
        </Box>
      )}
    </Box>
  );
};
